// Adopted from https://tanstack.com/query/latest/docs/framework/react/guides/advanced-ssr
// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import { cache } from 'react';

import {
  QueryCache,
  QueryClient,
  defaultShouldDehydrateQuery,
  isServer,
  matchQuery,
} from '@tanstack/react-query';
import { SuperJSON } from 'superjson';

// Makes sure we share one array per request. This will only work on the server, but that's fine since we only use this to track hydration.
const getHydratedQueryKeys = cache((): (readonly unknown[])[] => []);

function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (error instanceof Error && error.message.startsWith('AbortError')) {
          console.log('Aborted', query.queryKey);
          return;
        }

        console.error(error, query.queryKey);
      },
    }),
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
      },
      hydrate: {
        deserializeData: (data) => SuperJSON.parse(data),
      },
      dehydrate: {
        serializeData: (data) => SuperJSON.stringify(data),
        // include pending queries in dehydration
        shouldDehydrateQuery: (query) => {
          const shouldDehydrate =
            defaultShouldDehydrateQuery(query) ||
            query.state.status === 'pending';

          if (
            process.env.NODE_ENV !== 'development' ||
            shouldDehydrate === false
          ) {
            return shouldDehydrate;
          }

          const alreadyDehydrated = getHydratedQueryKeys().some((key) =>
            matchQuery(
              {
                queryKey: key,
                exact: true,
              },
              query,
            ),
          );

          if (alreadyDehydrated === true) {
            if (process.env.NODE_ENV === 'development') {
              // I know this log is kinda spammy, but leaving here as we try this out. Feel free to remove.
              console.debug(
                `Skipping hydration as query has already been dehydrated.
                This is normal as we're sharing a query client across requests and possibly using multiple hydration boundaries on a page.
                (entire queryclient gets passed to hydration boundary, meaning the same query key can be present each time the client gets passed)
                However, if you're seeing unexpected data, this might be the cause.
                Always make sure that a query can not have different data for the same query key.
                `,
                query.queryKey,
              );
            }
            return false;
          }

          getHydratedQueryKeys().push(query.queryKey);

          return true;
        },
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

// On the server we make a query client per request. Cache has no impact on browser side, but there the query client is only made once and shared via context.
export const getQueryClient = cache(() => {
  if (isServer) {
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
});
